import axios from 'axios';

/**
 * Centralized backend-availability monitor.
 *
 * The user is automatically logged out after {@link CONTINUOUS_FAILURE_MS} of *uninterrupted*
 * connectivity failure. Failures are fed centrally from two sources so every API request is
 * monitored in one place:
 *   • the global axios response interceptor (live API traffic), and
 *   • a lightweight heartbeat that also covers idle sessions (see `ServerHeartbeat` in App.tsx).
 *
 * On the first failure we arm a single expiry timer AND start a fast `/ping` recovery probe:
 *   • if the server becomes reachable again within the window, the state resets — no logout;
 *   • if it stays unreachable, the timer fires exactly once → {@link onExpire} clears the
 *     session and redirects to the Login page.
 *
 * Scenarios covered (see {@link isUnavailabilityError}): API service stopped, server terminated,
 * network disconnected (connection refused / ERR_NETWORK), and HTTP 502 / 503 / 504. Client-side
 * request aborts (route changes) and timeouts are deliberately NOT treated as outages, so normal
 * navigation between modules can never trigger a false logout.
 */

const CONTINUOUS_FAILURE_MS = 5000; // auto-logout after this much uninterrupted failure
const PROBE_MS = 1000;              // first recovery probe; each later one waits twice as long
const PROBE_TIMEOUT_MS = 4000;      // per-probe timeout

type ExpireHandler = (reason: string) => void;

let pingUrl = '';
let onExpire: ExpireHandler = () => {};

let firstFailureAt: number | null = null;
let expireTimer: ReturnType<typeof setTimeout> | null = null;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probeDelay = PROBE_MS;
let expired = false;

/** Wires the monitor to the app's ping endpoint and logout handler. Called once from api.client. */
export function configureServerMonitor(opts: { pingUrl: string; onExpire: ExpireHandler }): void {
  pingUrl = opts.pingUrl;
  onExpire = opts.onExpire;
}

/**
 * True only when an axios error is POSITIVE evidence the backend is unreachable — never for a
 * normal HTTP error (400/401/403/404/409/422 … the server clearly answered) and, critically,
 * never for a client-side abort or timeout.
 *
 * Why aborts/timeouts are excluded: navigating between modules (ID Card Studio, Photos, …) makes
 * the browser cancel the previous view's in-flight requests, which axios surfaces as ECONNABORTED
 * ("Request aborted") with no response. Media-heavy screens also saturate the browser's 6-socket
 * pool, so a slow endpoint can time out (also ECONNABORTED). Neither means the server is down, so
 * neither may start the auto-logout clock — that was the false-logout-on-navigation bug.
 *
 * Genuine unavailability = a proxy reporting a dead backend (502/503/504) OR a hard connection
 * failure (ERR_NETWORK: connection refused, DNS failure, browser offline). A stopped API process
 * refuses the connection and fails fast with ERR_NETWORK, so real outages are still caught.
 */
export function isUnavailabilityError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (axios.isCancel(error)) return false;

  const status = error.response?.status;
  // Reverse proxy in front of a dead/overloaded backend → genuine unavailability.
  if (status === 502 || status === 503 || status === 504) return true;
  // Any other real HTTP response proves the server is alive and responding.
  if (error.response) return false;

  // No HTTP response. Only a hard connection failure counts as "down"; a client-side abort or
  // timeout (both ECONNABORTED / ERR_CANCELED) is navigation/slow-endpoint noise, not an outage.
  const code = error.code;
  if (code === 'ERR_CANCELED' || code === 'ECONNABORTED') return false;

  // ERR_NETWORK and other no-response connection failures → backend unreachable.
  return true;
}

/** Report that the server responded — clears any degraded state / pending logout. */
export function reportServerReachable(): void {
  firstFailureAt = null;
  expired = false;
  probeDelay = PROBE_MS;
  if (expireTimer) { clearTimeout(expireTimer); expireTimer = null; }
  stopProbe();
}

/** Report that a request failed in a way that indicates the backend is unreachable. */
export function reportServerUnreachable(): void {
  if (expired) return;
  if (firstFailureAt !== null) return; // a failure window is already running
  firstFailureAt = Date.now();
  expireTimer = setTimeout(expire, CONTINUOUS_FAILURE_MS);
  scheduleProbe();
}

function scheduleProbe(): void {
  stopProbe();
  probeTimer = setTimeout(runProbe, probeDelay);
  probeDelay *= 2;
}

function stopProbe(): void {
  if (probeTimer) { clearTimeout(probeTimer); probeTimer = null; }
}

async function runProbe(): Promise<void> {
  if (expired || firstFailureAt === null) return;
  try {
    // Bare axios (not the intercepted client) so probing never feeds itself.
    await axios.get(pingUrl, { timeout: PROBE_TIMEOUT_MS });
    reportServerReachable();                 // recovered within the window → no logout
  } catch (err) {
    if (isUnavailabilityError(err)) {
      if (!expired && firstFailureAt !== null) scheduleProbe(); // still down → keep probing
    } else {
      reportServerReachable();               // got a real HTTP response → server is alive
    }
  }
}

function expire(): void {
  if (expired) return;
  expired = true;
  stopProbe();
  onExpire('server_unavailable');
}
